import { useResponseItemContext } from '@app/src/context/ResponseItemContext'
import SkipQuestionButton from '@app/src/pages/Questionnaire/Provider/Report/SkipQuestionButton'
import type { SxProps, Theme } from '@mui/material'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import AppliedSuggestionNote from './AppliedSuggestionNote'
import SuggestAnswerButton from './SuggestAnswerButton'
import type { QuestionSuggestionsCallbacks } from './useQuestionSuggestions'
import useQuestionSuggestions from './useQuestionSuggestions'

/* Bottom padding of the response content, owned here so the source band can cancel it and sit flush
   against the question card's bottom edge. */
export const RESPONSE_CONTENT_BOTTOM_PADDING = 4

const bandSx: SxProps<Theme> = theme => ({
  mb: -RESPONSE_CONTENT_BOTTOM_PADDING,
  borderTop: 1,
  borderColor: 'divider',
  borderBottomLeftRadius: theme.shape.borderRadius * 2,
  borderBottomRightRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
})

type QuestionSuggestionFooterProps = QuestionSuggestionsCallbacks & {
  isLoadingCloneFile?: boolean
}

const QuestionSuggestionFooter: FC<QuestionSuggestionFooterProps> = ({ isLoadingCloneFile, ...callbacks }) => {
  const { formatMessage } = useIntl()
  const { appliedSuggestion, hideInputField } = useResponseItemContext()
  const { observerRef, sources, isAutofilling, isLoadingSuggestions, openSuggestionDrawer } =
    useQuestionSuggestions(callbacks)

  const showSuggestions = !hideInputField && !appliedSuggestion
  const isRetrieving = showSuggestions && isAutofilling && isLoadingSuggestions

  return (
    <Stack ref={observerRef}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box flexGrow={1}>
          <SkipQuestionButton requestItem={callbacks.requestItem} />
        </Box>
        {showSuggestions && !isRetrieving && (
          <Box display="flex" mx={4} justifyContent="flex-end">
            <SuggestAnswerButton
              sources={sources}
              isLoadingSuggestions={isLoadingSuggestions}
              isApplying={isLoadingCloneFile}
              onSelectSource={openSuggestionDrawer}
            />
          </Box>
        )}
      </Box>
      {isRetrieving && (
        <Box sx={bandSx}>
          <Stack direction="row" spacing={1.5} alignItems="center" px={4} py={1.5} bgcolor="grey.100">
            <CircularProgress size={15} color="inherit" sx={{ color: 'text.secondary' }} />
            <Typography color="textSecondary">
              {formatMessage({ id: 'suggestedAnswers.retrievingFromFiles' })}
            </Typography>
          </Stack>
        </Box>
      )}
      {appliedSuggestion && (
        <Box sx={bandSx}>
          <AppliedSuggestionNote appliedSuggestion={appliedSuggestion} onView={openSuggestionDrawer} />
        </Box>
      )}
    </Stack>
  )
}

export default QuestionSuggestionFooter
